/**
 * The effectiveness slice of the wire — Task PW2 (pillar web), consuming
 * `GET /api/v1/effectiveness` and `GET /api/v1/effectiveness/team`, both already
 * built and tested against `openapi.yaml`'s `Effectiveness` tag (Task P3). Hand-
 * written to mirror the contract, exactly as `presence.ts`'s own header describes
 * for its slice: transcribe what the screen reads, not the whole document, and
 * this task does not touch `openapi.yaml`.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE RESEARCH GUARDRAIL, CARRIED INTO THE TYPES THEMSELVES
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `src/modules/effectiveness/effectiveness.ts`'s own header states it for the
 * server; the same three facts hold here, in the shapes a screen reads:
 *
 *  1. **Goals are primary; `productivity` is secondary context.**
 *     `EffectivenessProductivityView.is_secondary_context` is typed `true` — a
 *     literal, not a `boolean` — so a screen cannot read a row where the server
 *     forgot to say so; it does not exist on this side of the wire either.
 *  2. **Never a fabricated `0`.** `achievement_pct`, `productivity.value` and
 *     `on_time_rate` are all `number | null`. `null` is "we have nothing to say",
 *     never rendered as `0` — the same `PRODUCT.md` §4 discipline every other
 *     nullable metric in this codebase keeps.
 *  3. **The team read is a DISTRIBUTION, never a ranked list.**
 *     `EffectivenessTeamResponse` carries quartiles and counts and nothing keyed
 *     by employee — there is no field here a screen could sort people by, because
 *     the server never sent one.
 *
 * `EffectivenessFraming` restates the guardrail as data the server actually sent,
 * on both endpoints, so the screen has something to render beside its own copy
 * rather than only trusting a comment.
 */

import { qs, type QueryValue } from './paths';

export const EFFECTIVENESS_PATH = '/api/v1/effectiveness';
export const EFFECTIVENESS_TEAM_PATH = '/api/v1/effectiveness/team';

/** The level 02 §11 resolved the caller's field of view to, on either response. */
export type EffectivenessScopeLevel = 'self' | 'department' | 'company';

/**
 * `EffectivenessGoalStats` of `openapi.yaml` — `wp_hr_goals` reduced to counts for
 * the requested period, the PRIMARY effectiveness signal.
 */
export interface EffectivenessGoalStats {
  readonly total: number;
  readonly achieved: number;
  readonly in_progress: number;
  /** `null`, NEVER `0`, when `total` is zero — nothing assigned, nothing to say. */
  readonly achievement_pct: number | null;
}

/**
 * `EffectivenessProductivityView` of `openapi.yaml` — the EXISTING explainable,
 * confidence-bounded productivity score, read verbatim, never recomputed. Secondary
 * context beside `EffectivenessGoalStats`, never a quota — `is_secondary_context`
 * is `true` on the wire, always, and typed as the literal for the reason this
 * file's header gives.
 */
export interface EffectivenessProductivityView {
  /** `null`, never `0`, when no score's period overlaps the requested window. */
  readonly value: number | null;
  readonly confidence: number;
  readonly data_completeness: number;
  readonly missing_data_flags: readonly string[];
  readonly period_start: string | null;
  readonly period_end: string | null;
  readonly is_secondary_context: true;
}

/** `EffectivenessAttendanceStats` of `openapi.yaml` — the same `calculateAttendance` chain `/attendance` uses. */
export interface EffectivenessAttendanceStats {
  readonly expected_days: number;
  readonly present_days: number;
  readonly on_time_days: number;
  /** `null` when `expected_days` is zero — nobody was rostered, nothing to rate. */
  readonly on_time_rate: number | null;
  readonly data_completeness: number;
}

/**
 * `EffectivenessExplanationFlag` of `openapi.yaml` — why one figure on a row
 * should be read carefully rather than as a settled number. Closed set.
 */
export const EFFECTIVENESS_EXPLANATION_FLAGS = [
  'low_confidence_score',
  'no_productivity_score',
  'no_goal_data',
  'incomplete_attendance_data',
] as const;

export type EffectivenessExplanationFlag = (typeof EFFECTIVENESS_EXPLANATION_FLAGS)[number];

/**
 * `EffectivenessRow` of `openapi.yaml` — one employee's effectiveness read for the
 * requested period. Goals first, productivity second (secondary context, always
 * with its confidence), attendance third — the same order this screen renders.
 */
export interface EffectivenessRow {
  readonly employee_id: string;
  readonly full_name: string | null;
  readonly department_id: string | null;
  readonly goals: EffectivenessGoalStats;
  readonly productivity: EffectivenessProductivityView;
  readonly attendance: EffectivenessAttendanceStats;
  readonly explanation_flags: readonly EffectivenessExplanationFlag[];
}

/** `EffectivenessPeriod` of `openapi.yaml`. */
export interface EffectivenessPeriod {
  readonly from: string;
  readonly to: string;
}

/**
 * `EffectivenessFraming` of `openapi.yaml` — the 2026 deep-research guardrail, as
 * data rather than only as documentation. Every value is a server build-time
 * constant; a screen cannot render either endpoint as a leaderboard or a quota
 * without contradicting a field the server itself sent. The three booleans are
 * typed as their literal, exactly as `EFFECTIVENESS_FRAMING` fixes them server-side.
 */
export interface EffectivenessFraming {
  readonly is_ranking: false;
  readonly is_quota: false;
  readonly is_auto_decision: false;
  readonly purpose: string;
}

/** `EffectivenessListResponse` of `openapi.yaml` — `GET /api/v1/effectiveness`. */
export interface EffectivenessListResponse {
  readonly data: readonly EffectivenessRow[];
  readonly period: EffectivenessPeriod;
  readonly scope: { readonly level: EffectivenessScopeLevel };
  readonly framing: EffectivenessFraming;
}

/**
 * `EffectivenessQuartileSummary` of `openapi.yaml` — a five-number summary over
 * one figure across the scope, a DISTRIBUTION never a ranked list: nothing here
 * can be turned back into "who is #1".
 */
export interface EffectivenessQuartileSummary {
  readonly count: number;
  readonly min: number;
  readonly p25: number;
  readonly median: number;
  readonly p75: number;
  readonly max: number;
}

/**
 * `EffectivenessTeamResponse` of `openapi.yaml` — `GET /api/v1/effectiveness/team`.
 * The distribution of goal-achievement and productivity score across the caller's
 * resolved scope, for spotting who needs SUPPORT — never a punitive ranked list.
 * There is no member here keyed by employee; that is the whole point (this file's
 * header, item 3).
 */
export interface EffectivenessTeamResponse {
  readonly member_count: number;
  /** `null` when no member of the scope has a goal-achievement figure at all. */
  readonly goal_achievement: EffectivenessQuartileSummary | null;
  /** `null` when no member of the scope has a productivity score. */
  readonly productivity_score: EffectivenessQuartileSummary | null;
  readonly low_confidence_count: number;
  readonly no_goal_data_count: number;
  readonly no_productivity_score_count: number;
  readonly period: EffectivenessPeriod;
  readonly scope: { readonly level: EffectivenessScopeLevel };
  readonly framing: EffectivenessFraming;
}

/** The filters both `GET /effectiveness` and `GET /effectiveness/team` accept. */
export interface EffectivenessQuery {
  readonly departmentId?: string | undefined;
  readonly dateFrom?: string | undefined;
  readonly dateTo?: string | undefined;
}

/**
 * `GET /effectiveness?department_id=&date_from=&date_to=`.
 *
 * All three are **server** filters, intersected with the caller's resolved scope —
 * never applied client-side over a page already in hand (`paths.ts`'s own rule for
 * every narrowing in this file). `x-wp-pagination: none` on both endpoints, so
 * there is no `limit` to choose: every row in scope arrives in the one response.
 */
export function effectivenessPath(query: EffectivenessQuery = {}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    department_id: query.departmentId,
    date_from: query.dateFrom,
    date_to: query.dateTo,
  };
  return `${EFFECTIVENESS_PATH}${qs(params)}`;
}

/**
 * `GET /effectiveness/team?department_id=&date_from=&date_to=`.
 *
 * The SAME three filters `effectivenessPath` accepts — `openapi.yaml` documents
 * this endpoint as answering over "the same scope and the same period" the list
 * read uses, so the two requests a screen issues for one view must be able to
 * share exactly the same query values.
 */
export function effectivenessTeamPath(query: EffectivenessQuery = {}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    department_id: query.departmentId,
    date_from: query.dateFrom,
    date_to: query.dateTo,
  };
  return `${EFFECTIVENESS_TEAM_PATH}${qs(params)}`;
}
